import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import Hls from 'hls.js'
import { createHlsConfig } from './hlsConfig'

interface HlsPlayerProps {
  src: string | null
  autoPlay?: boolean
  controls?: boolean
  muted?: boolean
  playsInline?: boolean
  width?: string | number
  height?: string | number
  className?: string
  style?: React.CSSProperties
  videoClassName?: string
  videoStyle?: React.CSSProperties
  onError?: (error: any) => void
  onClick?: (e: React.MouseEvent) => void
  lowLatency?: boolean
}

export interface HlsPlayerMethods {
  play: () => Promise<void>
  pause: () => void
  getVideoElement: () => HTMLVideoElement | null
}

const HlsPlayer = forwardRef<HlsPlayerMethods, HlsPlayerProps>(
  (
    {
      src,
      autoPlay = false,
      controls = false,
      muted = true,
      playsInline = true,
      width = '100%',
      height = '100%',
      className = '',
      style = {},
      videoClassName = '',
      videoStyle = {},
      onError,
      onClick,
      lowLatency = true,
    },
    ref
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const hlsRef = useRef<Hls | null>(null)

    useImperativeHandle(ref, () => ({
      play: async () => {
        if (!videoRef.current) return
        try {
          await videoRef.current.play()
        } catch (e) {
          console.error('Play failed:', e)
        }
      },
      pause: () => {
        videoRef.current?.pause()
      },
      getVideoElement: () => videoRef.current,
    }))

    useEffect(() => {
      const video = videoRef.current
      if (!video || !src) return

      // Clean up previous instance
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }

      // Check for native HLS support (Safari)
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src
        if (autoPlay) {
          requestAnimationFrame(() => {
            video.play().catch(e => console.warn('Native HLS autoplay failed:', e))
          })
        }
        return
      }

      // Use HLS.js
      if (!Hls.isSupported()) {
        onError?.(new Error('HLS is not supported in this browser'))
        return
      }

      const config = createHlsConfig({}, lowLatency ? 'lowLatency' : 'default')
      const hls = new Hls(config)
      hlsRef.current = hls

      hls.attachMedia(video)

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src)
      })

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        if (autoPlay) {
          requestAnimationFrame(() => {
            video.play().catch(e => console.warn('HLS.js autoplay failed:', e))
          })
        }
      })

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (!data.fatal) return
        
        console.error('HLS fatal error:', data)
        
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            setTimeout(() => hls.startLoad(), 1000)
            break
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError()
            break
          default:
            hls.destroy()
            onError?.(data)
            break
        }
      })

      // Cleanup
      return () => {
        if (hlsRef.current) {
          hlsRef.current.destroy()
          hlsRef.current = null
        }
      }
    }, [src, autoPlay, onError, lowLatency])

    const containerStyle = {
      width,
      height,
      ...(src ? { position: 'relative' as const } : { backgroundColor: '#000' }),
      ...style,
    }

    const videoProps = {
      ref: videoRef,
      className: videoClassName,
      style: {
        width: '100%',
        height: '100%',
        ...videoStyle,
      },
      controls,
      muted,
      playsInline,
      ...(src && { autoPlay }),
    }

    return (
      <div
        className={className}
        style={containerStyle}
        onClick={onClick}
      >
        <video {...videoProps} />
      </div>
    )
  }
)

HlsPlayer.displayName = 'HlsPlayer'

export default HlsPlayer